import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BarChart3, Database, Building2, Users } from "lucide-react";
import { Card } from "../components/ui/card";
import BiFilters from "./bi/BiFilters";
import { BiKpis, BiOrders, BiFinance } from "./bi/BiKpis";
import BiTrend from "./bi/BiTrend";
import BiBreakdown from "./bi/BiBreakdown";
import BiProductDetail from "./bi/BiProductDetail";
import BiExclusions from "./bi/BiExclusions";
import BiCustomerFocus from "./bi/BiCustomerFocus";
import { fmtInt } from "./bi/biShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);
const readFocus = () => { try { return JSON.parse(sessionStorage.getItem("bi_focus") || "null") || { members: [], group: null }; } catch { return { members: [], group: null }; } };

const AdminBIDashboard = ({ onNavigate }) => {
  const [filters, setFilters] = useState({ preset: "month", date_from: today().slice(0, 8) + "01", date_to: today(), granularity: "auto" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [exclOpen, setExclOpen] = useState(false);
  const [focusOpen, setFocusOpen] = useState(false);
  const [focus, setFocus] = useState(readFocus);
  const [reloadKey, setReloadKey] = useState(0);

  const params = useMemo(() => {
    const p = { preset: filters.preset, granularity: filters.granularity };
    if (filters.preset === "custom") { p.date_from = filters.date_from; p.date_to = filters.date_to; }
    const ids = focus.members.filter((m) => m.customer_id).map((m) => m.customer_id);
    const names = focus.members.filter((m) => !m.customer_id).map((m) => m.name);
    if (ids.length) p.focus_ids = ids.join(",");
    if (names.length) p.focus_names = names.join("|");
    return p;
  }, [filters, focus]);
  const applyFocus = (members, group) => { const next = { members, group }; setFocus(next); sessionStorage.setItem("bi_focus", JSON.stringify(next)); };

  useEffect(() => {
    if (filters.preset === "custom" && (!filters.date_from || !filters.date_to)) return;
    let alive = true;
    setLoading(true);
    axios.get(`${API}/admin/bi`, { params }).then((r) => { if (alive) setData(r.data); }).catch(() => toast.error("Errore caricamento BI")).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [params, filters.preset, filters.date_from, filters.date_to, reloadKey]);

  const excluded = data?.sources?.excluded_names || [];

  return (
    <div className="space-y-5" data-testid="bi-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-stone-900 flex items-center gap-2"><BarChart3 className="w-6 h-6 text-[#8A1C2A]" /> Business Intelligence</h2>
          <p className="text-stone-500 text-sm">Un'unica vista: vendite dai DDT, conteggio ordini, finanza. Tocca <span className="inline-block align-middle text-stone-400">ⓘ</span> su ogni numero per sapere da dove deriva.</p>
        </div>
        <div className="flex gap-2 shrink-0">
        <button type="button" onClick={() => setFocusOpen(true)} className={`h-11 px-4 rounded-xl border text-sm font-semibold inline-flex items-center gap-2 ${focus.members.length ? "border-[#8A1C2A] bg-[#8A1C2A]/5 text-[#8A1C2A]" : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"}`} data-testid="bi-focus-btn">
          <Users className="w-4 h-4" /> {focus.members.length ? (focus.group || `${focus.members.length} client${focus.members.length === 1 ? "e" : "i"}`) : "Per cliente"}
        </button>
        <button type="button" onClick={() => setExclOpen(true)} className={`h-11 px-4 rounded-xl border text-sm font-semibold inline-flex items-center gap-2 shrink-0 ${excluded.length ? "border-amber-300 bg-amber-50 text-amber-900" : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"}`} data-testid="bi-exclusions-btn">
          <Building2 className="w-4 h-4" /> Esclusioni{excluded.length ? ` (${excluded.length})` : ""}
        </button>
        </div>
      </div>

      <BiFilters filters={filters} onChange={setFilters} period={data?.period} />

      {!data && loading && <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>}

      {data && (
        <div className={`space-y-5 transition-opacity ${loading ? "opacity-60" : ""}`}>
          {focus.members.length > 0 && (
            <p className="text-xs text-[#8A1C2A] bg-[#8A1C2A]/5 border border-[#8A1C2A]/20 rounded-xl px-3 py-2" data-testid="bi-focus-banner">
              Statistiche solo per {focus.group ? <>il gruppo <b>{focus.group}</b> ({focus.members.length} clienti)</> : <b>{focus.members.map((m) => m.name).join(", ")}</b>} — {fmtInt(data.sources.focus?.docs || 0)} DDT su {fmtInt(data.sources.focus?.docs_total || 0)} nel periodo.{" "}
              <button type="button" onClick={() => applyFocus([], null)} className="underline font-semibold" data-testid="bi-focus-banner-reset">Tutti i clienti</button>
            </p>
          )}
          {excluded.length > 0 && (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="bi-exclusions-banner">
              Esclusi dalle statistiche: <b>{excluded.join(", ")}</b> — {fmtInt(data.sources.excluded_docs)} DDT nel periodo{data.finance?.excluded_docs && !focus.members.length ? ` e ${fmtInt(data.finance.excluded_docs)} documenti fiscali` : ""}.
            </p>
          )}
          <BiKpis data={data} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BiOrders data={data} />
            <BiFinance data={data} onOpenStatements={() => onNavigate?.("statements")} />
          </div>
          <BiTrend data={data} />
          <BiBreakdown data={data} onPickProduct={setProduct} />
          <Card className="bg-stone-50 border-stone-200 p-4 text-xs text-stone-600 space-y-1" data-testid="bi-sources-footer">
            <p className="font-semibold text-stone-800 flex items-center gap-1.5"><Database className="w-3.5 h-3.5" /> Da dove derivano i numeri</p>
            <p><b>Vendite, kg, prodotti, clienti, agenti</b> — {data.notes.ddt} Nel periodo: {fmtInt(data.sources.ddt_used)} DDT ({fmtInt(data.sources.etifood_docs)} import Etifood + {fmtInt(data.sources.etiflor_docs)} sync Etiflor, {fmtInt(data.sources.duplicates_removed)} doppioni esclusi{data.sources.excluded_docs ? `, ${fmtInt(data.sources.excluded_docs)} DDT di clienti esclusi` : ""}), {fmtInt(data.sources.lines)} righe.</p>
            <p><b>IVA inclusa</b> — {data.notes.vat}</p>
            <p><b>Prodotti</b> — {data.notes.products}</p>
            <p><b>Ordini</b> — {data.notes.orders}</p>
            <p><b>Finanza</b> — {data.notes.finance}</p>
            <p><b>Esclusioni</b> — {data.notes.exclusions}{excluded.length ? ` Attuali: ${excluded.join(", ")}.` : " Nessun cliente escluso."}</p>
          </Card>
        </div>
      )}

      <BiProductDetail product={product} filters={params} onClose={() => setProduct(null)} />
      <BiCustomerFocus open={focusOpen} onClose={() => setFocusOpen(false)} selection={focus.members} onApply={applyFocus} />
      <BiExclusions open={exclOpen} onClose={() => setExclOpen(false)} onChanged={() => setReloadKey((k) => k + 1)} note={data?.notes?.exclusions} />
    </div>
  );
};

export default AdminBIDashboard;
